import { useI18n } from "@/i18n/I18nProvider";
import { Reveal } from "@/hooks/useReveal";
import { SEO } from "@/components/SEO";
import { Brain, Boxes, Smartphone, Link2, Layers, ArrowUpRight, Check, Rocket, Target, Workflow, Globe2, Building2 } from "lucide-react";
import { Link } from "react-router-dom";

const items = [
  { key: "ai", Icon: Brain },
  { key: "web", Icon: Boxes },
  { key: "mobile", Icon: Smartphone },
  { key: "chain", Icon: Link2 },
  { key: "custom", Icon: Layers },
] as const;

export default function Services() {
  const { t } = useI18n();
  return (
    <>
      <SEO title={t.nav.services} description={t.services.subtitle} />

      <section className="mx-auto max-w-7xl px-6 lg:px-10 pt-24 lg:pt-32 pb-20">
        <Reveal>
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-6">— {t.services.eyebrow}</p>
        </Reveal>
        <Reveal delay={120}>
          <h1 className="font-display text-5xl lg:text-8xl font-semibold tracking-tighter leading-[0.95] max-w-4xl text-balance">
            {t.services.title}
          </h1>
        </Reveal>
        <Reveal delay={240}>
          <p className="mt-8 max-w-2xl text-lg lg:text-xl text-muted-foreground leading-relaxed">
            {t.services.subtitle}
          </p>
        </Reveal>
      </section>

      <section className="border-t hairline">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          {items.map(({ key, Icon }, i) => {
            const item = (t.services.items as Record<string, { t: string; d: string; b: string[] }>)[key];
            const reverse = i % 2 === 1;
            return (
              <Reveal key={key} delay={50}>
                <div className={`grid lg:grid-cols-12 gap-10 py-16 lg:py-24 border-b hairline last:border-b-0 ${reverse ? "lg:[direction:rtl]" : ""}`}>
                  <div className="lg:col-span-5 [direction:ltr]">
                    <div className="flex items-center gap-4 mb-6">
                      <span className="text-xs font-mono text-muted-foreground">0{i + 1}</span>
                      <span className="h-px flex-1 bg-border" />
                      <Icon className="h-6 w-6" strokeWidth={1.4} />
                    </div>
                    <h2 className="font-display text-4xl lg:text-5xl font-semibold tracking-tighter">
                      {item.t}
                    </h2>
                  </div>
                  <div className="lg:col-span-7 [direction:ltr]">
                    <p className="text-lg lg:text-xl text-muted-foreground leading-relaxed">{item.d}</p>
                    <p className="mt-8 text-xs uppercase tracking-wider text-muted-foreground mb-4">
                      {t.services.benefits}
                    </p>
                    <ul className="grid sm:grid-cols-2 gap-3">
                      {item.b.map((bullet) => (
                        <li key={bullet} className="flex items-start gap-3 text-sm">
                          <Check className="h-4 w-4 mt-0.5 shrink-0 text-foreground" strokeWidth={2} />
                          <span>{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* CTA - Iniciar un proyecto */}
      <section className="border-t hairline">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 text-center">
          <Reveal>
            <Link to="/contact" className="inline-flex h-14 items-center gap-2 rounded-full bg-foreground px-8 text-base font-medium text-background hover:opacity-90 transition">
              {t.cta.start}
              <ArrowUpRight className="h-5 w-5" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* VENTURE PROGRAM */}
      <VenturesSection />
    </>
  );
}

function VenturesSection() {
  const { t } = useI18n();
  const V = (t.services as any).ventures as {
    eyebrow: string;
    tagline: string;
    title: string;
    intro1: string;
    intro2: string;
    investBlockTitle: string;
    investBlockBody: string;
    investBullets: string[];
    investBlockClose: string;
    investTimingNote: string;
    modelTitle: string;
    modelBody1: string;
    modelBody2: string;
    modelStepsTitle: string;
    modelSteps: string[];
    modelClose: string;
    cta: string;
  };

  const stepIcons = [Target, Workflow, Boxes, Building2, Globe2];

  return (
    <section className="border-t hairline relative overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,hsl(var(--foreground)/0.04),transparent_70%)]" />
      </div>

      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 lg:py-32">
        {/* Header */}
        <Reveal>
          <div className="flex items-center gap-4 mb-8">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl border hairline bg-background">
              <Rocket className="h-5 w-5" strokeWidth={1.4} />
            </span>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">— {V.eyebrow}</p>
          </div>
        </Reveal>

        <Reveal delay={80}>
          <p className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground mb-4">
            {V.tagline}
          </p>
        </Reveal>
        <Reveal delay={160}>
          <h2 className="font-display text-4xl lg:text-6xl font-semibold tracking-tighter leading-[1.02] max-w-4xl text-balance">
            {V.title}
          </h2>
        </Reveal>

        <div className="mt-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 space-y-6 text-lg text-muted-foreground leading-relaxed">
            <p>{V.intro1}</p>
            <p>{V.intro2}</p>
          </div>
        </div>

        {/* Investment opportunity */}
        <Reveal delay={120}>
          <div className="mt-16 lg:mt-20 rounded-3xl border hairline bg-foreground/[0.02] p-8 sm:p-10 lg:p-14">
            <div className="grid lg:grid-cols-12 gap-10">
              <div className="lg:col-span-5">
                <div className="flex items-center gap-3 mb-5">
                  <span className="text-xs font-mono text-muted-foreground">01</span>
                  <span className="h-px flex-1 bg-border" />
                </div>
                <h3 className="font-display text-3xl lg:text-4xl font-semibold tracking-tighter">
                  {V.investBlockTitle}
                </h3>
              </div>
              <div className="lg:col-span-7 space-y-6">
                <p className="text-base lg:text-lg text-muted-foreground leading-relaxed">
                  {V.investBlockBody}
                </p>
                <ul className="grid sm:grid-cols-2 gap-3">
                  {V.investBullets.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-sm">
                      <Check className="h-4 w-4 mt-0.5 shrink-0 text-foreground" strokeWidth={2} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-base lg:text-lg text-muted-foreground leading-relaxed">
                  {V.investBlockClose}
                </p>
                <p className="inline-flex items-center gap-2 rounded-full border hairline bg-background px-4 py-2 text-xs uppercase tracking-wider text-muted-foreground">
                  <span className="h-1.5 w-1.5 rounded-full bg-foreground" />
                  {V.investTimingNote}
                </p>
              </div>
            </div>
          </div>
        </Reveal>

        {/* Venture creation model */}
        <Reveal delay={120}>
          <div className="mt-12 grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <div className="flex items-center gap-3 mb-5">
                <span className="text-xs font-mono text-muted-foreground">02</span>
                <span className="h-px flex-1 bg-border" />
              </div>
              <h3 className="font-display text-3xl lg:text-4xl font-semibold tracking-tighter">
                {V.modelTitle}
              </h3>
              <p className="mt-6 text-base lg:text-lg text-muted-foreground leading-relaxed">
                {V.modelBody1}
              </p>
              <p className="mt-4 text-base lg:text-lg text-muted-foreground leading-relaxed">
                {V.modelBody2}
              </p>
            </div>
            <div className="lg:col-span-7">
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-5">
                {V.modelStepsTitle}
              </p>
              <ol className="space-y-3">
                {V.modelSteps.map((s, i) => {
                  const Icon = stepIcons[i % stepIcons.length];
                  return (
                    <li
                      key={s}
                      className="group flex items-start gap-4 rounded-2xl border hairline bg-background px-5 py-4 transition-colors hover:bg-foreground/[0.03]"
                    >
                      <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border hairline">
                        <Icon className="h-4 w-4" strokeWidth={1.5} />
                      </span>
                      <div className="min-w-0 pt-1">
                        <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-0.5">
                          0{i + 1}
                        </p>
                        <p className="text-sm lg:text-base text-foreground leading-snug">{s}</p>
                      </div>
                    </li>
                  );
                })}
              </ol>
              <p className="mt-6 text-sm text-muted-foreground leading-relaxed">{V.modelClose}</p>
            </div>
          </div>
        </Reveal>

        {/* CTA */}
        <Reveal delay={120}>
          <div className="mt-16 lg:mt-20 flex justify-center">
            <Link
              to="/ventures"
              className="group inline-flex h-14 items-center gap-2 rounded-full bg-foreground px-8 text-base font-medium text-background hover:opacity-90 transition"
            >
              {V.cta}
              <ArrowUpRight className="h-5 w-5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}